import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const loadLeaflet = () => new Promise((resolve, reject) => {
  if (window.L) { resolve(window.L); return; }
  if (!document.querySelector('link[href*="leaflet.css"]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  const existing = document.querySelector('script[src*="leaflet.js"]');
  if (existing) {
    existing.addEventListener('load', () => resolve(window.L));
    existing.addEventListener('error', reject);
    return;
  }
  const script = document.createElement('script');
  script.src = LEAFLET_JS;
  script.async = true;
  script.onload = () => resolve(window.L);
  script.onerror = () => reject(new Error('Falha ao carregar o mapa'));
  document.body.appendChild(script);
});

const haversine = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const bearingDeg = (lat1, lon1, lat2, lon2) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
    - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const CARDINALS = ['norte', 'nordeste', 'leste', 'sudeste', 'sul', 'sudoeste', 'oeste', 'noroeste'];
const cardinal = (deg) => CARDINALS[Math.round(deg / 45) % 8];

const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

export default function ComoChegar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { person } = location.state || {};
  const graveId = person?.grave_id || searchParams.get('jazigo');

  const [grave, setGrave] = useState(null);
  const [error, setError] = useState('');
  const [userPos, setUserPos] = useState(null);
  const [userAcc, setUserAcc] = useState(null);
  const [heading, setHeading] = useState(null);
  const [gpsError, setGpsError] = useState('');
  const [started, setStarted] = useState(false);

  const mapRef = useRef(null);
  const markersRef = useRef({});
  const lineRef = useRef(null);
  const gravePosRef = useRef(null);

  useEffect(() => {
    if (!graveId) { navigate('/', { replace: true }); return; }
    api.get(`/graves/${graveId}/location`)
      .then(({ data }) => {
        setGrave(data);
        gravePosRef.current = { lat: Number(data.latitude), lng: Number(data.longitude) };
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Localização GPS deste jazigo ainda não foi cadastrada.');
      });
  }, [graveId, navigate]);

  useEffect(() => {
    if (!grave || mapRef.current) return;
    loadLeaflet().then((L) => {
      const g = gravePosRef.current;
      const map = L.map('como-chegar-map').setView([g.lat, g.lng], 19);
      const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 21,
        maxNativeZoom: 19,
        attribution: '&copy; OpenStreetMap',
      });
      const satellite = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 21, maxNativeZoom: 18, attribution: 'Imagens &copy; Esri' }
      );
      const labels = L.tileLayer(
        'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 21, maxNativeZoom: 18 }
      );
      satellite.addTo(map);
      L.control.layers(
        { 'Satélite': satellite, 'Mapa': osm },
        { 'Rótulos de ruas': labels },
        { position: 'topright' }
      ).addTo(map);
      const graveIcon = L.divIcon({
        className: '',
        html: '<div style="background:#C53F3F;color:#fff;border-radius:50%;width:28px;height:28px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:14px">&#10013;</div>',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      markersRef.current.grave = L.marker([g.lat, g.lng], { icon: graveIcon })
        .addTo(map)
        .bindPopup(`<strong>Jazigo ${grave.number}</strong><br/>Quadra ${grave.block}${grave.sector ? ` — Setor ${grave.sector}` : ''}`);
      markersRef.current.grave.openPopup();
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 200);
    }).catch(() => setError('Não foi possível carregar o mapa. Verifique sua conexão.'));
  }, [grave]);

  const orientHandlerRef = useRef(null);

  const startNavigation = useCallback(() => {
    setStarted(true);
    if (!navigator.geolocation) {
      setGpsError('Este dispositivo não oferece suporte à localização.');
      return;
    }
    navigator.geolocation.watchPosition(
      (pos) => {
        setGpsError('');
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setUserAcc(pos.coords.accuracy);
        const L = window.L;
        const map = mapRef.current;
        if (!L || !map) return;
        const ll = [pos.coords.latitude, pos.coords.longitude];
        if (!markersRef.current.user) {
          const userIcon = L.divIcon({
            className: '',
            html: '<div style="background:#1E6FD9;color:#fff;border-radius:50%;width:22px;height:22px;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:11px">&#9679;</div>',
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          });
          markersRef.current.user = L.marker(ll, { icon: userIcon, zIndexOffset: 1000 }).addTo(map).bindPopup('Você está aqui');
        } else {
          markersRef.current.user.setLatLng(ll);
        }
        const g = gravePosRef.current;
        if (g) {
          if (lineRef.current) lineRef.current.setLatLngs([ll, [g.lat, g.lng]]);
          else lineRef.current = L.polyline([ll, [g.lat, g.lng]], { color: '#F47729', weight: 3, dashArray: '6 8' }).addTo(map);
          map.panTo(ll, { animate: true, duration: 0.5 });
        }
      },
      (err) => {
        setGpsError(
          err.code === 1
            ? 'Permissão de localização negada. Libere o acesso nas permissões do navegador.'
            : 'Não foi possível obter sua posição. Verifique se o GPS está ativo.'
        );
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 }
    );

    const onOrient = (e) => {
      let h = null;
      if (typeof e.webkitCompassHeading === 'number') h = e.webkitCompassHeading;
      else if (e.alpha != null) h = e.alpha;
      if (h != null) setHeading(((h % 360) + 360) % 360);
    };
    orientHandlerRef.current = onOrient;
    if (typeof DeviceOrientationEvent !== 'undefined'
      && typeof DeviceOrientationEvent.requestPermission === 'function') {
      DeviceOrientationEvent.requestPermission()
        .then((s) => {
          if (s === 'granted') {
            window.addEventListener('deviceorientationabsolute', onOrient, true);
            window.addEventListener('deviceorientation', onOrient, true);
          }
        })
        .catch(() => {});
    } else {
      window.addEventListener('deviceorientationabsolute', onOrient, true);
      window.addEventListener('deviceorientation', onOrient, true);
    }
  }, []);

  useEffect(() => () => {
    const h = orientHandlerRef.current;
    if (h) {
      window.removeEventListener('deviceorientationabsolute', h, true);
      window.removeEventListener('deviceorientation', h, true);
    }
  }, []);

  const distance = userPos && gravePosRef.current
    ? haversine(userPos.lat, userPos.lng, gravePosRef.current.lat, gravePosRef.current.lng)
    : null;
  const bearing = userPos && gravePosRef.current
    ? bearingDeg(userPos.lat, userPos.lng, gravePosRef.current.lat, gravePosRef.current.lng)
    : null;
  const arrived = distance != null && distance < 5;
  const lowSignal = userAcc != null && userAcc > 20;
  const arrowRotation = heading != null && bearing != null ? bearing - heading : null;

  if (error) {
    return (
      <div className="results-page">
        <div className="container results-container">
          <span className="back-link" onClick={() => navigate(-1)}>← Voltar</span>
          <div className="not-found">
            <h2>Localização indisponível</h2>
            <p>{error}</p>
            <p style={{ marginTop: 8, fontSize: 13, color: 'var(--texto-secundario)' }}>
              Quadra {grave?.block || person?.block || '—'}{grave?.sector || person?.sector ? ` — Setor ${grave.sector || person.sector}` : ''} — Jazigo {grave?.number || person?.number || '—'}
            </p>
            <button className="btn-primary" onClick={() => navigate('/')} style={{ marginTop: 16 }}>VOLTAR</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="container results-container">
        <span className="back-link" onClick={() => navigate(-1)}>← Voltar</span>

        <div className="como-chegar-header">
          <h2>Como chegar ao jazigo</h2>
          <p>
            Quadra <strong>{grave?.block || '—'}</strong>
            {grave?.sector ? <> — Setor <strong>{grave.sector}</strong></> : null}
            {' — '}Jazigo <strong>{grave?.number || '—'}</strong>
            {person?.name ? <><br /><span style={{ color: 'var(--texto-secundario)' }}>{person.name}</span></> : null}
          </p>
        </div>

        <div className="como-chegar-nav">
          {arrived ? (
            <div className="como-chegar-arrived">
              <strong>Você chegou!</strong> Confira a placa do jazigo acima.
            </div>
          ) : distance != null ? (
            <>
              <div className="como-chegar-arrow" aria-hidden="true">
                <div
                  className="como-chegar-arrow-rot"
                  style={{ transform: `rotate(${arrowRotation != null ? arrowRotation : bearing}deg)` }}
                >
                  ↑
                </div>
              </div>
              <div className="como-chegar-info">
                <div className="como-chegar-dist">{fmtDist(distance)}</div>
                <div className="como-chegar-dir">siga para {cardinal(bearing)}</div>
                {arrowRotation != null && (
                  <div className="como-chegar-turn">
                    {Math.abs(arrowRotation) < 20 ? '✓ retifique' : arrowRotation > 0 ? 'gire à direita' : 'gire à esquerda'}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="como-chegar-hint">
              Toque em “INICIAR NAVEGAÇÃO” e caminhe em direção ao jazigo.
              A distância e a direção serão atualizadas em tempo real.
            </p>
          )}
        </div>

        {lowSignal && !arrived && (
          <div className="como-chegar-warn">
            Sinal GPS fraco (precisão de {Math.round(userAcc)} m) — confie mais na placa do que no número na tela.
          </div>
        )}
        {gpsError && <p className="gps-error">{gpsError}</p>}

        {!started && !arrived && (
          <button className="btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={startNavigation}>
            INICIAR NAVEGAÇÃO
          </button>
        )}

        <div id="como-chegar-map" className="como-chegar-map" />

        <p className="como-chegar-legal">
          A precisão depende do sinal GPS no local. Em áreas cobertas por árvores ou construções,
          use a numeração física do cemitério como referência final.
        </p>
      </div>
    </div>
  );
}
